/**
 * Saved Carts Dual-Write Test
 *
 * Tests that saved cart operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * SC1: Create saved cart
 * SC2: Update saved cart
 * SC3: Delete saved cart
 */

import {
  saveSavedCart,
  deleteSavedCart,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom, organizationIdAtom } from '../global/AtomsProvider';

// Test variation definitions
export const SAVED_CARTS_TEST_VARIATIONS = [
  { id: 'SC1', category: 'create', name: 'Create saved cart' },
  { id: 'SC2', category: 'update', name: 'Update saved cart name' },
  { id: 'SC3', category: 'delete', name: 'Delete saved cart' },
];

// Get test categories for UI grouping
export function getSavedCartsTestCategories() {
  return [
    { id: 'create', name: 'Create Saved Cart', variations: ['SC1'] },
    { id: 'update', name: 'Update Saved Cart', variations: ['SC2'] },
    { id: 'delete', name: 'Delete Saved Cart', variations: ['SC3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getSavedCartsTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const db = jotaiStore.get(dbAtom);
  const organizationId = jotaiStore.get(organizationIdAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Get first product for cart items
  const productEntries = Object.entries(productsDict || {});
  const product = productEntries.length > 0
    ? { id: productEntries[0][0], ...productEntries[0][1] }
    : { id: 'test-product', name: 'Test Product', price: 100 };

  return {
    organization,
    store,
    product,
    db,
    organizationId,
  };
}

/**
 * Compare saved cart via backend API
 */
async function compareSavedCartViaApi(cartId, cartData, log) {
  try {
    const response = await api.get(`/saved-carts/compare/${cartId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        cartId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'saved_cart',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    if (cartData.name) {
      const sbName = normalizeValue(supabaseData?.name);
      const fsName = normalizeValue(cartData.name);
      if (!valuesMatch(sbName, fsName)) {
        differences.push({
          field: 'name',
          supabaseValue: sbName,
          firestoreValue: fsName,
          severity: 'critical',
        });
      }
    }

    // Compare items count (lenient comparison for JSONB)
    const sbItemsCount = Array.isArray(supabaseData?.items) ? supabaseData.items.length : 0;
    const fsItemsCount = Array.isArray(cartData.items) ? cartData.items.length : 0;
    if (sbItemsCount !== fsItemsCount) {
      differences.push({
        field: 'items_count',
        supabaseValue: sbItemsCount,
        firestoreValue: fsItemsCount,
        severity: 'warning',
      });
    }

    return {
      cartId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      cartId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if saved cart was deleted from Supabase
 */
async function checkSavedCartDeleted(cartId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/saved-carts/compare/${cartId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single saved cart test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, product, organizationId } = testData;

  let createdRef = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'SC1': {
        // Create saved cart
        const cartData = {
          store: store.id,
          organizationId,
          name: `Test Cart ${Date.now()}`,
          items: [{
            id: product.id,
            name: product.name || 'Test Product',
            quantity: 2,
            price: product.price || 100,
          }],
        };

        log(`Creating saved cart: ${cartData.name}`);
        createdRef = await saveSavedCart(db, cartData);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareSavedCartViaApi(createdRef.id, cartData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test saved cart...');
        await deleteSavedCart(createdRef);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'SC2': {
        // Update saved cart test
        const cartData = {
          store: store.id,
          organizationId,
          name: `Original Cart ${Date.now()}`,
          items: [{
            id: product.id,
            name: product.name || 'Test Product',
            quantity: 1,
            price: product.price || 100,
          }],
        };

        log('Creating saved cart to update...');
        createdRef = await saveSavedCart(db, cartData);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareSavedCartViaApi(createdRef.id, cartData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Saved cart was not created in Supabase';
          break;
        }

        // Now update via Firestore
        const newName = `Updated Cart ${Date.now()}`;
        log(`Updating saved cart name to: ${newName}`);
        await createdRef.update({ name: newName });

        // Also update via API for Supabase
        await api.patch(`/saved-carts/${createdRef.id}`, { name: newName });

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare updated values
        const updateCheck = await compareSavedCartViaApi(createdRef.id, { ...cartData, name: newName }, log);
        result.comparisonResult = updateCheck;
        result.passed = updateCheck.foundInSupabase && valuesMatch(updateCheck.supabaseData?.name, newName);

        // Cleanup
        log('Cleaning up test saved cart...');
        await deleteSavedCart(createdRef);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'SC3': {
        // Delete saved cart test
        const cartData = {
          store: store.id,
          organizationId,
          name: `Cart to Delete ${Date.now()}`,
          items: [{
            id: product.id,
            name: product.name || 'Test Product',
            quantity: 3,
            price: product.price || 100,
          }],
        };

        log('Creating saved cart to delete...');
        createdRef = await saveSavedCart(db, cartData);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareSavedCartViaApi(createdRef.id, cartData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Saved cart was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting saved cart...');
        await deleteSavedCart(createdRef);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkSavedCartDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          cartId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        // No cleanup needed - already deleted
        createdRef = null;

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRef) {
      try {
        await deleteSavedCart(createdRef);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all saved carts tests
 */
export async function runFullSavedCartsTest(log = console.log, selectedVariations = null) {
  log('Starting saved carts dual-write tests...');

  const testData = getSavedCartsTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('SAVED CARTS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || SAVED_CARTS_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  SAVED_CARTS_TEST_VARIATIONS,
  getSavedCartsTestCategories,
  getSavedCartsTestData,
  runFullSavedCartsTest,
};
